import { useEffect, useMemo, useRef, useState } from 'react';
import type { MessageAttachment } from '@itsappy/shared';
import { Spinner } from '@/components/ui';
import { cn } from '@/lib/utils';

/** Playback speeds, cycled by one button rather than hidden in a menu. */
const SPEEDS = [1, 1.5, 2] as const;

const BAR_COUNT = 48;

function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds)) return '0:00';
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * A placeholder shape for voice notes recorded before waveforms were
 * captured. Derived from the attachment id so it is stable across renders and
 * different between messages — a row of identical bars reads as "broken",
 * and a random one flickers on every re-render.
 */
function fallbackPeaks(seed: string): number[] {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return Array.from({ length: BAR_COUNT }, (_, i) => {
    hash = (hash * 1103515245 + 12345) & 0x7fffffff;
    // Bias towards the middle of the range: speech is rarely silent or peaked.
    return 0.25 + ((hash >> 8) % 100) / 200 + Math.sin(i / 3) * 0.05;
  });
}

/** Resamples captured peaks to the number of bars actually drawn. */
function resample(peaks: number[], count: number): number[] {
  if (peaks.length === count) return peaks;
  const step = peaks.length / count;
  return Array.from({ length: count }, (_, i) => {
    const start = Math.floor(i * step);
    const end = Math.max(start + 1, Math.floor((i + 1) * step));
    let max = 0;
    for (let j = start; j < end && j < peaks.length; j += 1) max = Math.max(max, peaks[j]);
    return max;
  });
}

export interface VoiceNoteProps {
  attachment: MessageAttachment;
  /** Absent for a message still in flight, or when the viewer cannot ask. */
  onTranscribe?: () => void;
  transcribing?: boolean;
}

/**
 * The voice-note player.
 *
 * The waveform is drawn from peaks captured while recording rather than
 * decoded here: decoding every voice note in a scrolled channel would fetch
 * and decode megabytes of audio nobody has pressed play on.
 */
export function VoiceNote({ attachment, onTranscribe, transcribing }: VoiceNoteProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState((attachment.durationMs ?? 0) / 1000);
  const [showTranscript, setShowTranscript] = useState(false);

  const bars = useMemo(
    () =>
      attachment.waveform?.length
        ? resample(attachment.waveform, BAR_COUNT)
        : fallbackPeaks(attachment.id),
    [attachment.waveform, attachment.id],
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.playbackRate = speed;
  }, [speed]);

  const progress = duration > 0 ? Math.min(1, elapsed / duration) : 0;

  function toggle() {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      document.querySelectorAll<HTMLAudioElement>('audio[data-chat-voice-note]').forEach((other) => {
        if (other !== audio && !other.paused) other.pause();
      });
      audio.playbackRate = speed;
      void audio.play();
    } else {
      audio.pause();
    }
  }

  /** Click anywhere on the waveform to seek there. */
  function seekTo(event: React.MouseEvent<HTMLButtonElement>) {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
    setElapsed(audio.currentTime);
  }

  const transcript = attachment.transcript?.trim();
  const canAskForTranscript =
    !transcript && attachment.transcriptStatus !== 'unavailable' && Boolean(onTranscribe);

  return (
    <div className="w-full max-w-sm rounded-xl bg-surface-2 p-2 ring-1 ring-surface-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? 'Pause voice note' : 'Play voice note'}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-600 text-white transition-colors hover:bg-brand-500"
        >
          {playing ? (
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="h-4 w-4 translate-x-px" fill="currentColor" aria-hidden>
              <path d="M8 5.5v13a1 1 0 0 0 1.5.87l11-6.5a1 1 0 0 0 0-1.74l-11-6.5A1 1 0 0 0 8 5.5Z" />
            </svg>
          )}
        </button>

        <button
          type="button"
          onClick={seekTo}
          aria-label="Seek within the voice note"
          className="flex h-9 min-w-0 flex-1 items-center gap-[2px] rounded-md px-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          {bars.map((peak, index) => {
            const played = index / bars.length <= progress;
            return (
              <span
                key={index}
                className={cn(
                  'w-full rounded-full transition-colors',
                  played ? 'bg-brand-400' : 'bg-surface-4',
                )}
                style={{ height: `${Math.max(3, Math.min(1, peak) * 28)}px` }}
              />
            );
          })}
        </button>

        <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
          {formatClock(playing || elapsed > 0 ? elapsed : duration)}
        </span>

        <button
          type="button"
          onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length])}
          aria-label={`Playback speed ${speed}×`}
          title="Playback speed"
          className="shrink-0 rounded-md px-1.5 py-1 text-[11px] font-medium tabular-nums text-slate-300 transition-colors hover:bg-surface-3"
        >
          {speed}×
        </button>
      </div>

      {(transcript || canAskForTranscript) && (
        <div className="mt-1.5 border-t border-surface-3 pt-1.5">
          {transcript ? (
            <>
              <button
                type="button"
                onClick={() => setShowTranscript((open) => !open)}
                aria-expanded={showTranscript}
                className="text-[11px] font-medium text-brand-300 transition-colors hover:text-brand-200"
              >
                {showTranscript ? 'Hide transcript' : 'Show transcript'}
              </button>
              {showTranscript && (
                <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-300">
                  {transcript}
                  {attachment.transcriptSource === 'browser' && (
                    <span className="ml-1 text-slate-500">(auto, rough)</span>
                  )}
                </p>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={onTranscribe}
              disabled={transcribing}
              className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400 transition-colors hover:text-brand-200 disabled:opacity-60"
            >
              {transcribing && <Spinner className="h-3 w-3" />}
              {transcribing ? 'Transcribing…' : 'Transcribe'}
            </button>
          )}
        </div>
      )}

      <audio
        ref={audioRef}
        src={attachment.url}
        preload="metadata"
        data-chat-voice-note
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setElapsed(0);
        }}
        onTimeUpdate={(event) => setElapsed(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          const value = event.currentTarget.duration;
          // Chromium reports Infinity for a MediaRecorder blob until it is
          // seeked; the recorded length we stored is the reliable one.
          if (Number.isFinite(value) && value > 0) setDuration(value);
        }}
        className="hidden"
      >
        Voice note
      </audio>
    </div>
  );
}
